import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type AppRole = 'super_admin' | 'platform_admin' | 'host' | 'venue_manager';

interface UserRole {
  id: string;
  role: AppRole;
  platform_id?: string;
  venue_id?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRoles: UserRole[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  hasPlatformRole: (role: AppRole, platformId?: string) => boolean;
  hasVenueAccess: (venueId: string) => boolean;
  isSuperAdmin: () => boolean;
  isPlatformAdmin: () => boolean;
  isHost: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Load user roles when user signs in
        if (session?.user) {
          setTimeout(() => {
            loadUserRoles(session.user.id);
          }, 0);
        } else {
          setUserRoles([]);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        loadUserRoles(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        console.error('Error loading user roles:', error);
        return;
      }

      setUserRoles(data || []);
    } catch (error) {
      console.error('Error in loadUserRoles:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const hasRole = (role: AppRole): boolean => {
    return userRoles.some(ur => ur.role === role);
  };

  const hasPlatformRole = (role: AppRole, platformId?: string): boolean => {
    return userRoles.some(ur => 
      ur.role === role && (!platformId || ur.platform_id === platformId)
    );
  };

  const hasVenueAccess = (venueId: string): boolean => {
    return userRoles.some(ur => 
      ur.role === 'super_admin' || 
      ur.venue_id === venueId ||
      (ur.role === 'platform_admin' && ur.platform_id) // Platform admins have access to all venues in their platform
    );
  };

  const isSuperAdmin = (): boolean => hasRole('super_admin');
  const isPlatformAdmin = (): boolean => hasRole('platform_admin');
  const isHost = (): boolean => hasRole('host') || hasRole('venue_manager');

  const value = {
    user,
    session,
    userRoles,
    loading,
    signIn,
    signUp,
    signOut,
    hasRole,
    hasPlatformRole,
    hasVenueAccess,
    isSuperAdmin,
    isPlatformAdmin,
    isHost,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}